// Light / dark / auto theme preference.
//
// The CSS already defines two token sets — light at :root and dark under .dark
// (src/index.css:11-36). This provider owns the class application: the user
// picks light, dark, or auto; auto follows the OS prefers-color-scheme media
// query when the browser supports it (Chrome 76+, so all modern browsers and
// Electron / iOS / Android 10+ Capacitor; falls back to light on the Android
// 6 WebView which is Chrome 53).
//
// The actual class is also pre-applied by an inline script in public/index.html
// before the React bundle loads, so the page doesn't flash light-mode while
// hydrating into dark. Keep the localStorage key in sync between the two.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const ThemeContext = createContext(null);

export const STORAGE_KEY = 'steadily_theme_mode';
const VALID_MODES = ['light', 'dark', 'auto'];

const readStoredMode = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && VALID_MODES.includes(saved)) return saved;
  } catch (_) { /* private mode etc. */ }
  return 'auto';
};

const systemPrefersDark = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch (_) {
    return false;
  }
};

const resolveTheme = (mode) => {
  if (mode === 'dark') return 'dark';
  if (mode === 'light') return 'light';
  return systemPrefersDark() ? 'dark' : 'light';
};

const applyThemeClass = (theme) => {
  if (typeof document === 'undefined') return;
  const cls = document.documentElement.classList;
  if (theme === 'dark') cls.add('dark');
  else cls.remove('dark');
  // Keep the mobile system-UI tint in sync with the paper background. The
  // boot script in index.html sets this on first paint; this keeps it
  // accurate after subsequent toggles or live OS-theme changes.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', theme === 'dark' ? '#1A1614' : '#FDFBF7');
};

export const ThemeProvider = ({ children }) => {
  const [mode, setModeState] = useState(readStoredMode);
  const [effectiveTheme, setEffectiveTheme] = useState(() => resolveTheme(readStoredMode()));
  // Per-page theme override ('light' or null). The marketing homepage pins
  // itself light via setThemeOverride; owning class application here (rather
  // than the page touching classList) avoids the mount-order race where this
  // provider's effect ran after the page's and re-applied the dark class.
  const [override, setOverride] = useState(null);

  // Re-resolve whenever the preference changes.
  useEffect(() => {
    setEffectiveTheme(resolveTheme(mode));
  }, [mode]);

  // Single owner of the dark class: apply the override if set, otherwise the
  // user's effective theme. Clearing the override restores the user's theme.
  useEffect(() => {
    applyThemeClass(override || effectiveTheme);
  }, [override, effectiveTheme]);

  // In auto mode, follow OS theme changes live.
  useEffect(() => {
    if (mode !== 'auto') return;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    let mql;
    try { mql = window.matchMedia('(prefers-color-scheme: dark)'); }
    catch (_) { return; }
    const handle = (e) => {
      setEffectiveTheme(e.matches ? 'dark' : 'light');
    };
    // addEventListener is the modern API; older Safari / older Capacitor
    // WebViews need addListener instead.
    if (typeof mql.addEventListener === 'function') mql.addEventListener('change', handle);
    else if (typeof mql.addListener === 'function') mql.addListener(handle);
    return () => {
      if (typeof mql.removeEventListener === 'function') mql.removeEventListener('change', handle);
      else if (typeof mql.removeListener === 'function') mql.removeListener(handle);
    };
  }, [mode]);

  const setMode = useCallback((next) => {
    if (!VALID_MODES.includes(next)) return;
    setModeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); }
    catch (_) { /* ignore */ }
  }, []);

  const setThemeOverride = useCallback((next) => {
    setOverride(next === 'light' ? 'light' : null);
  }, []);

  const value = useMemo(
    () => ({ mode, effectiveTheme, setMode, setThemeOverride }),
    [mode, effectiveTheme, setMode, setThemeOverride]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
};

export default ThemeContext;
